import { useCallback, useState } from 'react';
import { showError, showInfo } from '../../../shared/ui/appToast';
import { isDatabaseUnavailableError } from '../../../shared/localDb';
import { stockRowFromProductBranch } from '../../application/inventoryUtils';
import { isInventoriableProduct, type StockRow } from '../../../inventory/stock/public-api';
import type { Product } from '../../../catalog/domain-api';
import { useInventoryUiStore } from '../../store/index';
import { useShallow } from 'zustand/react/shallow';
import { handleInventoryFlowResult, isInventoryFlowDone } from '../../application/inventoryFlowBridge';
import type { InventoryRefreshHandlers } from '../../application/inventoryFlowBridge';
import { useInventoryOperationsController } from './useInventoryOperationsController';

type Params = {
  ctx: {
    currentBranchId: string | null | undefined;
    user: { id?: string } | null | undefined;
    canEdit: boolean;
    canAdjust?: boolean;
    canViewValuation?: boolean;
  };
  stocks: StockRow[];
  filteredStocks: StockRow[];
  refresh: InventoryRefreshHandlers;
  closeAdjustModal: () => void;
  closeCostCorrectModal: () => void;
  closeAddStockModal: () => void;
  closeActivityModal: () => void;
  openAddStockModalForProduct: (product: Product) => void;
  selectedProduct: Product | null;
  entryQuantity: string;
  entryUnitCost: string;
  entryMinQuantity: string;
  entryExpiry: string;
  entryReason: 'purchase_reception' | 'stock_restock' | 'stock_initial_load';
  entryLotNumber: string;
  entrySupplierId: string | null;
  entryVariantId: string | null;
  setSavingEntry: (v: boolean) => void;
};

export function useInventoryMutationsFlow({
  ctx,
  stocks,
  filteredStocks,
  refresh,
  closeAdjustModal,
  closeCostCorrectModal,
  closeAddStockModal,
  closeActivityModal,
  openAddStockModalForProduct,
  selectedProduct,
  entryQuantity,
  entryUnitCost,
  entryMinQuantity,
  entryExpiry,
  entryReason,
  entryLotNumber,
  entrySupplierId,
  entryVariantId,
  setSavingEntry,
}: Params) {
  const controller = useInventoryOperationsController();
  const branchId = ctx.currentBranchId ?? null;
  const editCtx = { canEdit: ctx.canEdit, canAdjust: ctx.canAdjust ?? ctx.canEdit, canViewValuation: ctx.canViewValuation ?? true, branchId, userId: ctx.user?.id ?? null };

  const { adjustModalVisible, adjustStockRow, adjustDelta, adjustReason, adjustSaving } = useInventoryUiStore(
    useShallow((s) => ({
      adjustModalVisible: s.adjustModalVisible,
      adjustStockRow: s.adjustStockRow,
      adjustDelta: s.adjustDelta,
      adjustReason: s.adjustReason,
      adjustSaving: s.adjustSaving,
    }))
  );
  const setAdjustDelta = useInventoryUiStore((s) => s.setAdjustDelta);
  const setAdjustReason = useInventoryUiStore((s) => s.setAdjustReason);
  const setAdjustSaving = useInventoryUiStore((s) => s.setAdjustSaving);
  const setAdjustStockRow = useInventoryUiStore((s) => s.setAdjustStockRow);
  const {
    costCorrectModalVisible,
    costCorrectStockRow,
    costCorrectInput,
    costCorrectSaving,
  } = useInventoryUiStore(
    useShallow((s) => ({
      costCorrectModalVisible: s.costCorrectModalVisible,
      costCorrectStockRow: s.costCorrectStockRow,
      costCorrectInput: s.costCorrectInput,
      costCorrectSaving: s.costCorrectSaving,
    }))
  );
  const setCostCorrectInput = useInventoryUiStore((s) => s.setCostCorrectInput);
  const setCostCorrectSaving = useInventoryUiStore((s) => s.setCostCorrectSaving);

  const [detailStock, setDetailStock] = useState<StockRow | null>(null);
  const [detailVisible, setDetailVisible] = useState(false);

  const openDetailModal = useCallback((stock: StockRow) => {
    setDetailStock(stock);
    setDetailVisible(true);
  }, []);

  const closeDetailModal = useCallback(() => {
    setDetailVisible(false);
    setDetailStock(null);
  }, []);

  const applyAdjustment = useCallback(async () => {
    if (adjustSaving) return;
    setAdjustSaving(true);
    try {
      const result = await controller.runAdjustStock(editCtx, {
        stockRow: adjustStockRow,
        adjustDelta,
        adjustReason,
      });
      const handled = await handleInventoryFlowResult(result, refresh);
      if (isInventoryFlowDone(handled)) closeAdjustModal();
    } finally {
      setAdjustSaving(false);
    }
  }, [adjustSaving, adjustDelta, adjustReason, adjustStockRow, closeAdjustModal, controller, editCtx, refresh, setAdjustSaving]);

  const applyCostCorrect = useCallback(async () => {
    if (costCorrectSaving) return;
    setCostCorrectSaving(true);
    try {
      const result = await controller.runCorrectStockCost(editCtx, {
        stockRow: costCorrectStockRow,
        costInput: costCorrectInput,
      });
      const handled = await handleInventoryFlowResult(result, refresh);
      if (isInventoryFlowDone(handled)) closeCostCorrectModal();
    } finally {
      setCostCorrectSaving(false);
    }
  }, [
    costCorrectSaving,
    closeCostCorrectModal,
    controller,
    costCorrectInput,
    costCorrectStockRow,
    editCtx,
    refresh,
    setCostCorrectSaving,
  ]);

  const handleAddStock = useCallback(async () => {
    if (useInventoryUiStore.getState().savingEntry) return;
    setSavingEntry(true);
    try {
      const result = await controller.runReceiveStock(editCtx, {
        product: selectedProduct,
        entryQuantity,
        entryUnitCost,
        entryMinQuantity,
        entryExpiry,
        entryReason,
        entryLotNumber,
        entrySupplierId,
        entryVariantId,
      });
      const handled = await handleInventoryFlowResult(result, refresh);
      if (isInventoryFlowDone(handled)) closeAddStockModal();
    } finally {
      setSavingEntry(false);
    }
  }, [
    closeAddStockModal,
    controller,
    editCtx,
    entryExpiry,
    entryLotNumber,
    entryMinQuantity,
    entryQuantity,
    entryReason,
    entrySupplierId,
    entryUnitCost,
    entryVariantId,
    refresh,
    selectedProduct,
    setSavingEntry,
  ]);

  const openStockFromBranchActivity = useCallback(
    (productId: string) => {
      if (!ctx.currentBranchId) {
        showError('Sucursal', 'Seleccioná una sucursal para abrir el stock.');
        return;
      }
      const fromLoadedList =
        stocks.find((s) => s.productId === productId) ?? filteredStocks.find((s) => s.productId === productId);
      if (fromLoadedList) {
        closeActivityModal();
        openDetailModal(fromLoadedList);
        return;
      }
      void (async () => {
        try {
          const [stockRow, product] = await Promise.all([
            controller.getStockByProductAndBranch(ctx.currentBranchId!, productId),
            controller.getProductById(productId),
          ]);
          if (!product) {
            showError('Producto no encontrado', 'No pudimos ubicar el producto en el catálogo.');
            return;
          }
          if (!isInventoriableProduct(product)) {
            showInfo('Sin stock físico', 'Este producto no lleva stock acá.');
            return;
          }
          if (!stockRow) {
            showInfo(
              'Sin stock cargado',
              `«${product.name}» no tiene stock en esta sucursal. Podés cargarlo con Ingresar stock.`
            );
            closeActivityModal();
            openAddStockModalForProduct(product);
            return;
          }
          closeActivityModal();
          openDetailModal(stockRowFromProductBranch(stockRow, product));
        } catch (error) {
          if (!isDatabaseUnavailableError(error)) {
            console.warn('[Inventory] No se pudo abrir stock desde actividad', error);
          }
          showError('No se pudo abrir', 'No pudimos cargar el detalle de stock de ese producto.');
        }
      })();
    },
    [closeActivityModal, controller, ctx.currentBranchId, filteredStocks, openAddStockModalForProduct, openDetailModal, stocks]
  );

  return {
    adjustModalVisible,
    adjustStockRow,
    setAdjustStockRow,
    adjustDelta,
    setAdjustDelta,
    adjustReason,
    setAdjustReason,
    adjustSaving,
    applyAdjustment,
    costCorrectModalVisible,
    costCorrectStockRow,
    costCorrectInput,
    setCostCorrectInput,
    costCorrectSaving,
    applyCostCorrect,
    detailStock,
    detailVisible,
    openDetailModal,
    closeDetailModal,
    openStockFromBranchActivity,
    handleAddStock,
  };
}
